import { Attribute } from '@lib/types/pets';

/**
 * Color priority mapping based on hex values from attribute categories
 * Priority: red first, amber 2nd, green 3rd, pink 4th, blue 5th, then others
 */
const COLOR_PRIORITY_MAP: Record<string, number> = {
  // Red - Critical/Major conditions and care instructions (Priority 1)
  '#cc0000': 1,

  // Amber/Orange - Minor conditions and warnings (Priority 2)
  '#b15500': 2,

  // Green - Medications, treatments, and positive attributes (Priority 3)
  '#0b7e64': 3,
  '#377C4B': 3, // Alternative green for "Permitted Treats"

  // Pink - Physical characteristics and preferences (Priority 4)
  '#c7328b': 4,

  // Blue - General characteristics and neutral traits (Priority 5)
  '#325ee2': 5,
};

/**
 * Sorts pet attributes by color priority first, then alphabetically within each color group
 *
 * Color priority order:
 * 1. Red (#cc0000) - Critical conditions, major allergies, extra-special care
 * 2. Amber (#b15500) - Minor conditions, minor allergies, minor behavioral traits
 * 3. Green (#0b7e64, #377C4B) - Medications, vaccinations, permitted treats
 * 4. Pink (#c7328b) - Physical characteristics, service preferences
 * 5. Blue (#325ee2) - General characteristics, neutral traits
 * 6. Other colors - Any unrecognized colors last
 *
 * @param attributes Array of pet attributes to sort
 * @returns Sorted array of attributes
 */
export const sortAttributesByColorAndName = (
  attributes: Attribute[]
): Attribute[] => {
  return attributes.sort((a, b) => {
    const colorA = a.category.textColour || '';
    const colorB = b.category.textColour || '';

    // Get priority for each color (999 for unrecognized colors)
    const priorityA = COLOR_PRIORITY_MAP[colorA] || 999;
    const priorityB = COLOR_PRIORITY_MAP[colorB] || 999;

    // First sort by color priority
    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    // If colors have the same priority, sort alphabetically by attribute name
    return a.name.localeCompare(b.name);
  });
};

/**
 * Get the priority number for a given hex color
 * @param hexColor The hex color string (e.g., "#cc0000")
 * @returns Priority number (1-5 for recognized colors, 999 for others)
 */
export const getColorPriority = (hexColor: string): number => {
  return COLOR_PRIORITY_MAP[hexColor] || 999;
};

/**
 * Get all unique hex colors grouped by priority for debugging/reference
 * @returns Object mapping priority levels to their hex colors
 */
export const getColorGroups = (): Record<number, string[]> => {
  const groups: Record<number, string[]> = {};

  Object.entries(COLOR_PRIORITY_MAP).forEach(([hexColor, priority]) => {
    if (!groups[priority]) {
      groups[priority] = [];
    }
    groups[priority].push(hexColor);
  });

  return groups;
};
